import { useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDebounce } from 'react-use';
import { useCreateManualJournalFromBillInvoice, useCreateManualJournalFromPurchaseOrder } from 'shared/data/webApp/v1';
import { useGetFields } from 'shared/data/webApp/v1';

import { ReportConfig } from '../../../../types/ReportConfig';
import { xeroAccrualManualJournalRequestIdState } from '../../XeroAccrualManualJournalPopup.states';
import { XeroAccrualManualJournalFormData } from './FormPopupContent.types';

export const useFormData = (reportConfig: ReportConfig) => {
    const setCreatedRequestId = useSetAtom(xeroAccrualManualJournalRequestIdState);

    const { mutateAsync: createManualJournalFromBillInvoice, isLoading: isLoadingFromBillInvoice } =
        useCreateManualJournalFromBillInvoice();
    const { mutateAsync: createManualJournalFromPurchaseOrder, isLoading: isLoadingFromPurchaseOrder } =
        useCreateManualJournalFromPurchaseOrder();

    const form = useForm<XeroAccrualManualJournalFormData>({
        defaultValues: {
            narration: '',
            date: undefined,
            accrualAccountId: null,
            remainingBalanceType: 'Net',
            unknownAmountHandlingType: 'Exclude',
        },
    });

    const submitFromBillInvoice = useCallback(
        (data: XeroAccrualManualJournalFormData) =>
            createManualJournalFromBillInvoice({
                body: {
                    companyId: reportConfig.companyId,
                    reportConfigId: reportConfig.id,
                    date: data.date,
                    narration: data.narration,
                    remainingBalanceType: data.remainingBalanceType,
                    accrualAccountId: data.accrualAccountId || '',
                },
            }),
        [createManualJournalFromBillInvoice, reportConfig.companyId, reportConfig.id]
    );

    const submitFromPurchaseOrder = useCallback(
        (data: XeroAccrualManualJournalFormData) =>
            createManualJournalFromPurchaseOrder({
                body: {
                    companyId: reportConfig.companyId,
                    reportConfigId: reportConfig.id,
                    date: data.date,
                    narration: data.narration,
                    remainingBalanceType: data.remainingBalanceType,
                    accrualAccountId: data.accrualAccountId || '',
                    unknownAmountHandlingType: data.unknownAmountHandlingType,
                },
            }),
        [createManualJournalFromPurchaseOrder, reportConfig.companyId, reportConfig.id]
    );

    const handleSubmit = useCallback(
        async (data: XeroAccrualManualJournalFormData) => {
            if (reportConfig.reportCode === domain.ReportCode.XeroBillInvoiceLine) {
                try {
                    const response = await submitFromBillInvoice(data);

                    setCreatedRequestId(response.requestId);
                } catch {
                    // do nothing
                }
            }

            if (reportConfig.reportCode === domain.ReportCode.XeroPurchaseOrderLine) {
                try {
                    const response = await submitFromPurchaseOrder(data);

                    setCreatedRequestId(response.requestId);
                } catch {
                    // do nothing
                }
            }
        },
        [reportConfig.reportCode, setCreatedRequestId, submitFromBillInvoice, submitFromPurchaseOrder]
    );

    return {
        form,
        handleFormSubmit: form.handleSubmit(handleSubmit),
        isLoading: isLoadingFromBillInvoice || isLoadingFromPurchaseOrder,
    };
};

export const useAccrualAccountData = (reportConfig: ReportConfig) => {
    const [query, setQuery] = useState('');
    const [queryValue, setQueryValue] = useState('');

    useDebounce(() => setQuery(queryValue), 600, [queryValue]);

    const integrationCode =
        reportConfig.reportCode === domain.ReportCode.XeroBillInvoiceLine
            ? domain.IntegrationCode.XeroBill
            : domain.IntegrationCode.XeroPo;

    const field = useSelector(
        (state) =>
            selectors.field.getFieldsBySystemPurpose(
                state,
                reportConfig.companyId,
                domain.FieldSystemPurpose.XeroAccount
            )[0]
    );

    const { data, isLoading } = useGetFields({
        query: {
            integrationCode,
            companyId: field.companyId,
            withValues: true,
            fieldsId: [field.id],
            query,
            startFrom: 0,
            rowNum: 116,
        },
    });

    const accrualAccounts = useMemo(() => {
        return data?.fields?.[0].exactValues || [];
    }, [data]);

    return {
        isLoadingAccrualAccounts: isLoading,
        accrualAccounts,
        onAccrualAccountsInputChange: setQueryValue,
    };
};
